import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  FlashOn as FlashOnIcon,
} from '@mui/icons-material';

interface AddFieldModalProps {
  open: boolean;
  onClose: () => void;
  onAddManually: () => void;
  onAutoAddMissing: () => void;
  missingRequiredCount: number;
}

export const AddFieldModal: React.FC<AddFieldModalProps> = ({
  open,
  onClose,
  onAddManually,
  onAutoAddMissing,
  missingRequiredCount,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Add Field Options
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          {/* Option 1: Add Single Field Manually */}
          <Paper
            sx={{
              p: 3,
              border: '2px solid',
              borderColor: 'grey.300',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
                transform: 'translateY(-2px)',
                boxShadow: 3,
              },
            }}
            onClick={() => {
              onAddManually();
              onClose();
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'primary.100',
                  color: 'primary.main',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AddIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Add Single Field Manually
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add one field at a time with a custom value
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Option 2: Auto-add Missing Required */}
          <Paper
            sx={{
              p: 3,
              border: '2px solid',
              borderColor: missingRequiredCount > 0 ? 'success.main' : 'grey.300',
              bgcolor: missingRequiredCount > 0 ? 'success.50' : 'grey.50',
              cursor: missingRequiredCount > 0 ? 'pointer' : 'not-allowed',
              opacity: missingRequiredCount > 0 ? 1 : 0.6,
              transition: 'all 0.2s',
              '&:hover': missingRequiredCount > 0
                ? {
                    borderColor: 'success.dark',
                    bgcolor: 'success.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  }
                : {},
            }}
            onClick={() => {
              if (missingRequiredCount > 0) {
                onAutoAddMissing();
                onClose();
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: missingRequiredCount > 0 ? 'success.main' : 'grey.400',
                  color: 'white',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FlashOnIcon fontSize="large" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Auto-add Missing Required Fields
                  </Typography>
                  {missingRequiredCount > 0 && (
                    <Box
                      sx={{
                        bgcolor: 'error.main',
                        color: 'white',
                        borderRadius: '12px',
                        px: 1,
                        py: 0.25,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {missingRequiredCount} missing
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const fieldPlural = missingRequiredCount > 1 ? 's' : '';
                    return missingRequiredCount > 0
                      ? `Automatically add all ${missingRequiredCount} missing required field${fieldPlural}`
                      : 'All required fields are already mapped or added';
                  })()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
